/**
 * TemplatesPage (`/templates`) — Bayenah V3 legal-template gallery.
 *
 * Categories:
 *   - عقود العمل
 *   - العقود التجارية
 *   - اتفاقيات السرية
 *   - مستندات الحوكمة
 *
 * Cards group by category when the "all" filter is active so the page
 * always renders ≥3 visible category sections (Test 12.2). Each card
 * keeps the primary "استخدام النموذج" CTA tucked under a hover reveal
 * (Test 12.4) and routes into the assistant with a pre-seeded prompt.
 */
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardList, Search } from 'lucide-react';

import { useLanguage } from '@/contexts/LanguageContext';
import { TERMS } from '@/lib/terminology';
import { cn } from '@/lib/utils';

const CATEGORIES = [
  { id: 'all',         ar: 'الكل',                en: 'All' },
  { id: 'employment',  ar: 'عقود العمل',          en: 'Employment' },
  { id: 'commercial',  ar: 'العقود التجارية',     en: 'Commercial' },
  { id: 'nda',         ar: 'اتفاقيات السرية',     en: 'NDAs' },
  { id: 'governance',  ar: 'مستندات الحوكمة',     en: 'Governance' },
];

const TEMPLATES = [
  { id: 'employment-fixed-term',   category: 'employment',  ar_title: 'عقد عمل محدد المدة',          en_title: 'Fixed-term employment contract', ar_desc: 'عقد عمل لمدة محددة وفق نظام العمل السعودي.',                              en_desc: 'Fixed-duration employment contract under Saudi Labor Law.',     prompt: 'صِغ عقد عمل محدد المدة وفق نظام العمل السعودي.' },
  { id: 'employment-indefinite',   category: 'employment',  ar_title: 'عقد عمل غير محدد المدة',      en_title: 'Indefinite employment contract', ar_desc: 'عقد توظيف دائم مع تنظيم فترة التجربة وحقوق نهاية الخدمة وفق نظام العمل السعودي.', en_desc: 'Permanent employment with probation and end-of-service rights.', prompt: 'صِغ عقد عمل غير محدد المدة بفترة تجربة 90 يوماً.' },
  { id: 'commercial-supply',       category: 'commercial',  ar_title: 'عقد توريد بضائع',             en_title: 'Goods supply agreement',         ar_desc: 'اتفاقية توريد متكررة بشروط دفع وتسليم وغرامات تأخير وفق نظام المعاملات المدنية.',     en_desc: 'Recurring supply agreement with payment, delivery and penalty clauses.', prompt: 'صِغ عقد توريد بضائع شهري بقيمة محددة وغرامة تأخير 2%.' },
  { id: 'commercial-services',     category: 'commercial',  ar_title: 'عقد تقديم خدمات',             en_title: 'Services agreement',             ar_desc: 'عقد خدمات بين مزوّد وعميل مع نطاق عمل ومراحل تسليم وفق نظام المعاملات المدنية.',          en_desc: 'Vendor–client services agreement with scope and milestones.',     prompt: 'صِغ عقد تقديم خدمات استشارية لمدة سنة بنطاق عمل واضح.' },
  { id: 'nda-mutual',              category: 'nda',         ar_title: 'اتفاقية سرية متبادلة',        en_title: 'Mutual NDA',                     ar_desc: 'حماية متبادلة للمعلومات السرية بين طرفين تجاريين وفق نظام المعاملات المدنية.',          en_desc: 'Two-way confidentiality protection between commercial parties.', prompt: 'صِغ اتفاقية سرية متبادلة بين شركتين تجاريتين بمدة 3 سنوات.' },
  { id: 'nda-employee',            category: 'nda',         ar_title: 'إقرار سرية للموظف',           en_title: 'Employee confidentiality',       ar_desc: 'إقرار يلتزم به الموظف بحماية الأسرار التجارية لصاحب العمل وفق نظام العمل السعودي.',     en_desc: 'Employee undertaking to safeguard the employer’s trade secrets.', prompt: 'صِغ إقرار سرية للموظف يغطي الأسرار التجارية وحقوق الملكية الفكرية.' },
  { id: 'governance-bylaws',       category: 'governance',  ar_title: 'لائحة داخلية لشركة',          en_title: 'Company internal bylaws',        ar_desc: 'لائحة تحكم العلاقة بين الشركاء وقواعد اتخاذ القرار وفق نظام الشركات.',                  en_desc: 'Bylaws governing partner relationships and decision-making.',     prompt: 'صِغ لائحة داخلية لشركة ذات مسؤولية محدودة بشريكين بنسبة 60/40.' },
  { id: 'governance-board-charter',category: 'governance',  ar_title: 'لائحة عمل مجلس الإدارة',      en_title: 'Board charter',                  ar_desc: 'تنظيم لاجتماعات وصلاحيات وقرارات مجلس الإدارة وفق نظام الشركات.',                       en_desc: 'Charter regulating board meetings, powers, and resolutions.',    prompt: 'صِغ لائحة عمل لمجلس إدارة شركة مساهمة مدرجة.' },
];

export default function TemplatesPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isArabic = language === 'ar';
  const [active, setActive] = useState('all');
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return TEMPLATES.filter((tpl) => {
      if (active !== 'all' && tpl.category !== active) return false;
      if (!q) return true;
      const hay = `${tpl.ar_title} ${tpl.en_title} ${tpl.ar_desc} ${tpl.en_desc}`.toLowerCase();
      return hay.includes(q);
    });
  }, [active, query]);

  const handleUse = (tpl) => {
    const prompt = isArabic ? tpl.prompt : `Draft: ${tpl.en_title}`;
    navigate(`/chat?prompt=${encodeURIComponent(prompt)}`);
  };

  // Group by category for the "all" view (Test 12.2 expects ≥3 groups).
  const groups = useMemo(() => {
    if (active !== 'all') return [];
    const buckets = CATEGORIES.filter((c) => c.id !== 'all').map((c) => ({
      ...c,
      templates: filtered.filter((t) => t.category === c.id),
    })).filter((g) => g.templates.length > 0);
    return buckets;
  }, [filtered, active]);

  return (
    <div className="min-h-full w-full" data-testid="templates-page" dir={isArabic ? 'rtl' : 'ltr'}>
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-center gap-2 mb-1">
          <ClipboardList className="w-5 h-5 text-[#111111]" strokeWidth={1.7} />
          <h1 className="text-3xl sm:text-4xl font-semibold text-[#111111] font-arabic">
            {isArabic ? TERMS.templates : 'Legal templates'}
          </h1>
        </div>
        <p className="text-[#666666] text-sm max-w-2xl mb-6 font-arabic">
          {isArabic
            ? 'مكتبة من النماذج القانونية الجاهزة للصياغة وفق الأنظمة السعودية.'
            : 'A library of ready-to-draft legal templates, aligned with Saudi regulations.'}
        </p>

        {/* Search */}
        <div
          className="relative mb-4 max-w-xl"
          data-testid="templates-search-wrap"
        >
          <Search
            className={cn(
              'absolute top-1/2 -translate-y-1/2 w-4 h-4 text-[#999999]',
              isArabic ? 'right-3' : 'left-3',
            )}
            strokeWidth={1.7}
          />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={isArabic ? 'ابحث عن نموذج…' : 'Search templates…'}
            className={cn(
              'w-full bg-white border border-[#E8E8E8] rounded-md py-2 text-[13px] text-[#111111] focus:outline-none focus:border-[#999999] font-arabic',
              isArabic ? 'pe-10 ps-3 text-right' : 'ps-10 pe-3',
            )}
            data-testid="templates-search-input"
          />
        </div>

        {/* Category filter */}
        <div
          className={cn('flex flex-wrap gap-2 mb-8', isArabic && 'flex-row-reverse')}
          data-testid="templates-filters"
        >
          {CATEGORIES.map((c) => {
            const isActive = active === c.id;
            return (
              <button
                key={c.id}
                onClick={() => setActive(c.id)}
                className={cn(
                  'px-3 py-1.5 rounded-full text-[13px] transition font-arabic',
                  isActive
                    ? 'bg-[#E8E8E8] text-[#111111] border border-[#E8E8E8]'
                    : 'bg-white text-[#666666] border border-[#E8E8E8] hover:text-[#111111]',
                )}
                data-testid={`templates-cat-${c.id}`}
              >
                {isArabic ? c.ar : c.en}
              </button>
            );
          })}
        </div>

        {filtered.length === 0 ? (
          <div
            className="text-center text-[#666666] text-sm py-16 font-arabic"
            data-testid="templates-empty"
          >
            {isArabic ? 'لا توجد نماذج مطابقة.' : 'No matching templates.'}
          </div>
        ) : active === 'all' ? (
          <div className="space-y-10" data-testid="templates-groups">
            {groups.map((g) => (
              <section key={g.id} data-testid={`templates-group-${g.id}`}>
                <h2
                  className={cn(
                    'text-[15px] font-semibold text-[#111111] mb-3 font-arabic',
                    isArabic && 'text-right',
                  )}
                >
                  {isArabic ? g.ar : g.en}{' '}
                  <span className="text-[#999999] text-[12px] font-normal">
                    · {g.templates.length}
                  </span>
                </h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {g.templates.map((tpl) => (
                    <TemplateCard
                      key={tpl.id}
                      tpl={tpl}
                      isArabic={isArabic}
                      onUse={() => handleUse(tpl)}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        ) : (
          <div
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
            data-testid="templates-grid"
          >
            {filtered.map((tpl) => (
              <TemplateCard
                key={tpl.id}
                tpl={tpl}
                isArabic={isArabic}
                onUse={() => handleUse(tpl)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TemplateCard({ tpl, isArabic, onUse }) {
  const categoryLabel = isArabic
    ? CATEGORIES.find((c) => c.id === tpl.category)?.ar
    : CATEGORIES.find((c) => c.id === tpl.category)?.en;
  return (
    <article
      className={cn(
        'group bg-white rounded-lg border border-[#E8E8E8] p-5 flex flex-col gap-3',
        'transition-colors duration-150 hover:border-[#999999]',
      )}
      data-testid={`template-card-${tpl.id}`}
    >
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wider text-[#999999] font-arabic">
          {categoryLabel}
        </span>
      </div>
      <h2
        className={cn(
          'text-[15px] font-medium text-[#111111] font-arabic',
          isArabic && 'text-right',
        )}
      >
        {isArabic ? tpl.ar_title : tpl.en_title}
      </h2>
      <p
        className={cn(
          'text-[12.5px] text-[#666666] leading-relaxed font-arabic flex-1',
          isArabic && 'text-right',
        )}
      >
        {isArabic ? tpl.ar_desc : tpl.en_desc}
      </p>
      <button
        type="button"
        onClick={onUse}
        className={cn(
          'self-stretch rounded-md bg-[#111111] text-white text-[13px] py-2 font-arabic',
          // Hover-reveal CTA per Test 12.4: hidden by default on hoverable
          // devices, fades in when the card is hovered or focused. Pointer
          // devices that can't hover (mobile / keyboard) keep it visible.
          'transition-opacity duration-150',
          'opacity-100 sm:opacity-0 group-hover:opacity-100 group-focus-within:opacity-100',
          'hover:bg-black/85',
        )}
        data-testid={`template-use-${tpl.id}`}
      >
        {isArabic ? 'استخدام النموذج' : 'Use template'}
      </button>
    </article>
  );
}
